import { Link } from 'react-router-dom'
import { Correct } from '../../components/Correct'
import { RefusedError } from '../../api/client'
import { useDailyBrief } from '../../hooks/useDailyBrief'
import { formatMoney } from '../../lib/format'
import { scopeWords } from '../../lib/orgs'
import type { BriefMeeting, BriefOpportunity, BriefReturnTo, DailyBrief } from '../../types/brief'
import './today.css'

// Today, at the top of the landscape: at most five meetings and at most five opportunities, ranked, and
// nothing else. The cap is the feature — the client's complaint about the brief he had was length — so this
// panel never grows a "show all", and it never pads: with nothing stalled it says so in a line.
// Everything here is read from the API; the browser does no arithmetic beyond formatting (ADR 0003 §4).

const DAY = new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'short', day: 'numeric' })
const TIME = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' })

export function Today() {
  const q = useDailyBrief()

  if (q.isError) {
    return (
      <div className="refusal" role="status">
        <strong>{q.error instanceof RefusedError ? q.error.refusal.reason : 'Today is unavailable'}</strong>
        <p>{q.error.message}</p>
      </div>
    )
  }
  if (!q.isSuccess) return null
  // Nothing on file at all is not a brief: the step card below says what to connect first.
  if (q.data.meetingsToday === 0 && q.data.openOpportunities === 0 && (q.data.returnTosDue ?? 0) === 0) return null
  return <TodayGlance brief={q.data} />
}

export function TodayGlance({ brief }: { brief: DailyBrief }) {
  const day = new Date(`${brief.today}T00:00:00`)
  return (
    <section className="glance today-glance" aria-label="Today">
      <div className="today-lead">
        <p className="kicker">Today · {DAY.format(day)}</p>
        <p className="today-headline">{brief.headline}</p>
        <dl className="glance-conventions">
          <dt>Meetings</dt>
          <dd>
            <span className="chip"><em>{brief.meetings.length}</em> of {brief.meetingsToday} left today</span>
          </dd>
          <dt>Pursuits</dt>
          <dd>
            <span className="chip"><em>{brief.opportunities.length}</em> of {brief.openOpportunities} in play</span>
            {brief.stalled > 0 && <span className="chip stalled"><em>{brief.stalled}</em> stalled</span>}
          </dd>
          {/* Whose day this is. Always said, never inferred: the numbers above are only true of one org. */}
          {scopeWords(brief.scope) && (
            <>
              <dt>Org</dt>
              <dd><span className="chip">{scopeWords(brief.scope)}</span></dd>
            </>
          )}
        </dl>
      </div>

      <div className="today-column">
        <p className="kicker">The meetings</p>
        {brief.meetings.length === 0
          ? <p className="today-quiet">Nothing left on the calendar today.</p>
          : (
            <ul className="today-list">
              {brief.meetings.map((m) => <MeetingRow key={m.meetingId} m={m} />)}
            </ul>
          )}
      </div>

      <div className="today-column">
        <p className="kicker">The biggest in play</p>
        {brief.opportunities.length === 0
          ? <p className="today-quiet">No open pursuit on file yet.</p>
          : (
            <ul className="today-list">
              {brief.opportunities.map((o) => <OpportunityRow key={o.engagementId} o={o} />)}
            </ul>
          )}
      </div>

      {/* The dates THEY named ("ping me in November"): shown only when one is here, capped like the rest (REQ-073). */}
      {(brief.returnTos?.length ?? 0) > 0 && (
        <div className="today-column">
          <p className="kicker">They said to come back{brief.returnTosDue > brief.returnTos.length ? ` · ${brief.returnTos.length} of ${brief.returnTosDue}` : ''}</p>
          <ul className="today-list">
            {brief.returnTos.map((r) => <ReturnToRow key={r.signalId} r={r} />)}
          </ul>
        </div>
      )}
    </section>
  )
}

function ReturnToRow({ r }: { r: BriefReturnTo }) {
  const when = r.overdue ? `${-r.daysLeft} day${r.daysLeft === -1 ? '' : 's'} past` : r.daysLeft === 0 ? 'today' : `in ${r.daysLeft} day${r.daysLeft === 1 ? '' : 's'}`
  return (
    <li className={`today-row pursuit${r.overdue ? ' stalled' : ''}`}>
      {r.overdue && <span className="today-mark" aria-hidden="true">!</span>}
      <div className="today-body">
        <Link className="today-title" to={`/opportunities/${encodeURIComponent(r.engagementId)}`}>{r.companyName}</Link>
        <span className="today-meta">{formatDay(r.dueOn)} · {when}</span>
        <span className="today-why">{r.what}</span>
        {r.quote && <span className="today-action">{r.quote}</span>}
      </div>
    </li>
  )
}

function formatDay(iso: string): string {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(new Date(`${iso}T00:00:00`))
}

function MeetingRow({ m }: { m: BriefMeeting }) {
  return (
    <li className={`today-row kind-${m.kind}`}>
      <span className="today-when">{TIME.format(new Date(m.start))}</span>
      <div className="today-body">
        <Link className="today-title" to="/meetings">{m.subject}</Link>
        <span className="today-meta">
          {m.companyName ?? (m.who.length > 0 ? m.who.join(', ') : roomWords(m.kind))}
          {m.decisionMakerInRoom && ' · decision maker'}
        </span>
        <span className="today-action">{m.action}</span>
        {m.priorWork && <span className="today-prior">{m.priorWork}</span>}
        {m.companyId && (
          m.prepWrittenAt
            ? <Link className="chip prep ready" to={`/accounts/${encodeURIComponent(m.companyId)}`} title="The prep brief the night wrote ahead of this meeting.">Prep ready</Link>
            : <span className="chip prep" title="The calendar sync writes the prep brief ahead of the meeting; it is not on file yet.">Prep on its way</span>
        )}
      </div>
    </li>
  )
}

function OpportunityRow({ o }: { o: BriefOpportunity }) {
  return (
    <li className={`today-row pursuit${o.stalled ? ' stalled' : ''}`}>
      {o.stalled && <span className="today-mark" aria-hidden="true">!</span>}
      <div className="today-body">
        <Link className="today-title" to={`/opportunities/${encodeURIComponent(o.engagementId)}`}>{o.companyName}</Link>
        <span className="today-meta">
          {o.amount ? formatMoney(o.amount) : 'no amount named'} · {o.stage}
          {o.stalled ? ` · quiet ${o.daysQuiet} days` : ''}
        </span>
        <span className="today-why">{o.why}</span>
        {o.involve.length > 0 && (
          <span className="today-involve">
            {o.involve.slice(0, 3).map((p) => (
              <span key={p.userId} className="chip person">{p.name}<em>{p.role}</em></span>
            ))}
            {o.involve.length > 3 && <span className="chip person">+{o.involve.length - 3} more</span>}
          </span>
        )}
        {o.action && <span className="today-action">{o.action}</span>}
        {o.nextStep && (
          <span className="today-correct">
            <span className="muted small">{o.nextStepOwner === 'me' ? 'You owe' : o.nextStepOwner === 'them' ? 'They owe' : 'Open'}: {o.nextStep}</span>
            <Correct kind="next-step" entityId={o.engagementId} entityLabel={o.nextStep} currentValue={o.nextStepOwner === 'me' ? 'mine' : o.nextStepOwner === 'them' ? 'theirs' : null} compact />
          </span>
        )}
      </div>
    </li>
  )
}

function roomWords(kind: string): string {
  if (kind === 'internal') return 'colleagues only'
  if (kind === 'solo') return 'no one else'
  return 'nobody on file yet'
}
